import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { CCollapse, CLink } from "@coreui/react";
import axios from "axios";
import Select from "react-select";
import config from "../../config/config";
import { toastSuccess, toastError } from "../../common/Toast";
import {
  CUSTOM_SELECT_DROPDOWN_STYLE,
  ERROR_MESSAGE,
  SUCCESS_MESSAGE,
} from "../../common/Constant";
import tinycolor from "tinycolor2";

// Color group configuration for organized rendering
const COLOR_GROUPS = [
  {
    name: "Primary Colors",
    colors: [
      // { label: "Primary Base Color", state: "primaryBaseColor", defaultValue: "#ffffff" },
      {
        label: "Primary Base Color",
        state: "primaryBaseColor",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Main Color",
        state: "primaryMainColor",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 800",
        state: "primaryColor800",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 400",
        state: "primaryColor400",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 200",
        state: "primaryColor200",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 100",
        state: "primaryColor100",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 50",
        state: "primaryColor50",
        defaultValue: "#ffffff",
      },
      {
        label: "Primary Color 25",
        state: "primaryColor25",
        defaultValue: "#ffffff",
      },
    ],
  },
  {
    name: "Secondary Colors",
    colors: [
      {
        label: "Secondary Main Color",
        state: "secondaryMainColor",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 800",
        state: "secondaryColor800",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 500",
        state: "secondaryColor500",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 400",
        state: "secondaryColor400",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 200",
        state: "secondaryColor200",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 100",
        state: "secondaryColor100",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 50",
        state: "secondaryColor50",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 25",
        state: "secondaryColor25",
        defaultValue: "#ffffff",
      },
      {
        label: "Secondary Color 10",
        state: "secondaryColor10",
        defaultValue: "#ffffff",
      },
    ],
  },
  {
    name: "Neutral Colors",
    colors: [
      {
        label: "Neutral Main Color",
        state: "neutralMainColor",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 800",
        state: "neutralColor800",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 400",
        state: "neutralColor400",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 200",
        state: "neutralColor200",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 100",
        state: "neutralColor100",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 50",
        state: "neutralColor50",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 25",
        state: "neutralColor25",
        defaultValue: "#ffffff",
      },
      {
        label: "Neutral Color 10",
        state: "neutralColor10",
        defaultValue: "#ffffff",
      },
    ],
  },
  {
    name: "Link Color",
    colors: [
      { label: "Link Color", state: "linkColor", defaultValue: "#ffffff" },
      {
        label: "Link Color Hover",
        state: "linkColorHover",
        defaultValue: "#ffffff",
      },
    ],
  },
];

const EMOJI_FIELDS = [
  { label: "First Emoji", state: "Emoji1" },
  { label: "Second Emoji", state: "Emoji2" },
  { label: "Third Emoji", state: "Emoji3" },
];

function Color({ visibleBannerChange, searchQuery, dynamicModule }) {
  const token = localStorage.getItem("Token");
  const [dynamicValue, setDynamicValue] = useState("MainSite");
  const [errors, setErrors] = useState({});
  const [themename, setThemeName] = useState("");
  const [themenameList, setThemeNameList] = useState([]);
  const [id, setId] = useState(0);
  const [themenameOptions, setThemenameOptions] = useState({
    label: " ",
    value: " ",
  });
  const [isClearable, setIsClearable] = useState(true);
  const [visibleBanners, setVisibleBanners] = useState(false);
  const [colorthemeList, setColorThemeList] = useState([]);

  // Initialize all color states dynamically
  const initialState = useMemo(() => {
    const state = {};
    COLOR_GROUPS.forEach((group) => {
      group.colors.forEach((color) => {
        state[color.state] = color.defaultValue;
      });
    });
    EMOJI_FIELDS.forEach((emoji) => {
      state[emoji.state] = "";
    });
    return state;
  }, []);

  const generateThemeShades = (groupType, baseColor) => {
    const base = tinycolor(baseColor);
    if (!base.isValid()) return {};

    const mix = (percent) =>
      tinycolor.mix(base, "#ffffff", percent).toHexString();

    switch (groupType.toLowerCase()) {
      case "primary":
        return {
          primaryMainColor: base.toHexString(),
          primaryMainColor: mix(15),
          primaryColor800: mix(20),
          primaryColor400: mix(28),
          primaryColor200: mix(37),
          primaryColor100: mix(50),
          primaryColor50: mix(65),
          primaryColor25: mix(80),
        };

      case "secondary":
        return {
          secondaryMainColor: base.toHexString(),
          secondaryColor800: mix(15),
          secondaryColor500: mix(20),
          secondaryColor400: mix(28),
          secondaryColor200: mix(37),
          secondaryColor100: mix(50),
          secondaryColor50: mix(65),
          secondaryColor25: mix(80),
          secondaryColor10: mix(97),
        };

      case "neutral":
        return {
          neutralMainColor: base.toHexString(),
          neutralColor800: mix(15),
          neutralColor400: mix(20),
          neutralColor200: mix(28),
          neutralColor100: mix(37),
          neutralColor50: mix(50),
          neutralColor25: mix(65),
          neutralColor10: mix(80),
        };

      default:
        return {};
    }
  };

  // Add emoji fields to initial state
  EMOJI_FIELDS.forEach((emoji) => {
    initialState[emoji.state] = "";
  });

  const [state, setState] = useState(initialState);

  // Memoized theme options for Select component
  const themeOptions = useMemo(
    () => themenameList.map((name) => ({ value: name, label: name })),
    [themenameList]
  );

  // Handle field changes generically
  const handleChange = (field, value) => {
    let newShades = {};

    if (field === "primaryMainColor") {
      newShades = generateThemeShades("primary", value);
    } else if (field === "secondaryMainColor") {
      newShades = generateThemeShades("secondary", value);
    } else if (field === "neutralMainColor") {
      newShades = generateThemeShades("neutral", value);
    }

    setState((prev) => ({
      ...prev,
      [field]: value,
      ...newShades,
    }));
  };

  // Reset all fields to default values
  const handleAddTheme = useCallback(() => {
    setId("");
    setThemeName("");
    setThemenameOptions({ label: "search theme", value: "search theme" });
    setIsClearable(false);
    setState(initialState);
  }, [initialState]);

  // Fetch theme list from API
  const fetchThemeList = useCallback(async () => {
    try {
      const res = await axios.get(
        `${config.ecommconfigBaseUrl}/ecomconfig/colorthemelist?from=${dynamicValue}`,
        {
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            Authorization: token,
          },
        }
      );

      const responseData = res.data.ResponseData || [];
      setColorThemeList(responseData);
      setThemeNameList(responseData.map((item) => item.themename));

      const activeTheme = responseData.find((item) => item.active === 1);
      if (activeTheme) {
        setId(activeTheme.id);
        setThemeName(activeTheme.themename);
        setThemenameOptions({
          label: activeTheme.themename,
          value: activeTheme.themename,
        });

        // Update all state values from active theme
        const updatedState = { ...initialState };
        COLOR_GROUPS.forEach((group) => {
          group.colors.forEach((color) => {
            updatedState[color.state] =
              activeTheme[color.state] || color.defaultValue;
          });
        });

        EMOJI_FIELDS.forEach((emoji) => {
          updatedState[emoji.state] = activeTheme[emoji.state] || "";
        });

        setState(updatedState);
      }
    } catch (error) {
      console.error("Error fetching color theme list:", error);
    }
  }, [dynamicValue, token, initialState]);

  useEffect(() => {
    if (dynamicModule) {
      setDynamicValue(dynamicModule);
    }
  }, [dynamicModule]);

  useEffect(() => {
    fetchThemeList();
  }, [fetchThemeList]);

  // Handle theme selection change
  const handleThemeChange = useCallback(
    (selectedTheme) => {
      const selectedThemeData = colorthemeList.find(
        (theme) => theme.themename === selectedTheme
      );

      if (selectedThemeData) {
        setId(selectedThemeData.id);
        setThemeName(selectedThemeData.themename);
        setThemenameOptions({
          label: selectedTheme,
          value: selectedTheme,
        });

        const updatedState = { ...state };
        COLOR_GROUPS.forEach((group) => {
          group.colors.forEach((color) => {
            updatedState[color.state] =
              selectedThemeData[color.state] || color.defaultValue;
          });
        });

        EMOJI_FIELDS.forEach((emoji) => {
          updatedState[emoji.state] = selectedThemeData[emoji.state] || "";
        });

        setState(updatedState);
      }
    },
    [colorthemeList, state]
  );

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!themename.trim()) {
      newErrors.themename = "Theme Name is required.";
    }
    return newErrors;
  }, [themename]);

  // Submit theme data
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const newErrors = validateForm();
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      const data = {
        id,
        themename,
        ...state,
        dynamicValue,
      };

      try {
        const res = await axios.post(
          `${config.ecommconfigBaseUrl}/ecomconfig/themeupsert`,
          data,
          {
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              "Access-Control-Allow-Origin": "*",
              Authorization: token,
            },
          }
        );

        if (res.data.ResponseCode === "SUCCESS") {
          toastSuccess(SUCCESS_MESSAGE);
          fetchThemeList();
        } else if (
          res.data.ResponseCode === "ERROR" &&
          res.data.ResponseID === -1
        ) {
          toastError(ERROR_MESSAGE);
        } else {
          toastError(ERROR_MESSAGE);
        }
      } catch (error) {
        console.error("Error submitting theme:", error);
      }
    },
    [id, themename, state, dynamicValue, token, validateForm, fetchThemeList]
  );

  // Apply active theme
  const handleApplySubmit = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        const res = await axios.post(
          `${config.ecommconfigBaseUrl}/ecomconfig/activetheme`,
          { id, from: dynamicValue },
          {
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              "Access-Control-Allow-Origin": "*",
              Authorization: token,
            },
          }
        );

        if (res.data.ResponseCode === "SUCCESS") {
          toastSuccess(SUCCESS_MESSAGE);
          fetchThemeList();
        } else {
          toastError(ERROR_MESSAGE);
        }
      } catch (error) {
        console.error("Error applying theme:", error);
      }
    },
    [id, dynamicValue, token, fetchThemeList]
  );

  // Render color input fields
  const renderColorInputs = (colors) => (
    <div className="row mb-3">
      {colors.map((color) => (
        <div className="col-6 col-md-4 col-lg-2" key={color.state}>
          <label>{color.label}</label>
          <input
            type="color"
            value={state[color.state]}
            onChange={(e) => handleChange(color.state, e.target.value)}
            className="form-control border-0"
          />

          <p>{state[color.state]}</p>
        </div>
      ))}
    </div>
  );

  // Render emoji inputs
  const renderEmojiInputs = () => (
    <div className="row mb-3 pt-3">
      <div className="col-12 text-start">
        <h6 className="pl-4">Emoji</h6>
      </div>
      {EMOJI_FIELDS.map((emoji) => (
        <div className="col-6 col-md-4 col-lg-3 d-flex" key={emoji.state}>
          <label className="mr-2 w-10">{emoji.label}</label>
          <input
            type="text"
            className="form-control"
            value={state[emoji.state]}
            onChange={(e) => handleChange(emoji.state, e.target.value)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      <div className="card">
        <div className="card-header">
          <h5 className="card-title">
            <CLink
              className="link"
              onClick={() => setVisibleBanners(!visibleBanners)}
            >
              Color & Theme
            </CLink>
          </h5>
        </div>
        <CCollapse visible={visibleBanners || visibleBannerChange}>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="text-center border border-secondary border-opacity-25">
                <div className="row mb-3 pt-3">
                  <div className="col-6 col-md-6 col-lg-2 d-flex">
                    <Select
                      id="themeSelect"
                      className="custom-search-select w-100 mr-3 ml-3"
                      options={themeOptions}
                      value={themenameOptions}
                      onChange={(selectedOption) => {
                        handleThemeChange(selectedOption?.value || "");
                      }}
                      isClearable={isClearable}
                      backspaceRemovesValue={false}
                      controlShouldRenderValue={true}
                      styles={CUSTOM_SELECT_DROPDOWN_STYLE}
                    />
                  </div>

                  <div className="col-6 col-md-4 col-lg-3 d-flex">
                    <button
                      className="btn btn-sm btn-primary mb-3 mr-3"
                      onClick={handleApplySubmit}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary mb-3"
                      onClick={handleAddTheme}
                    >
                      Add Theme
                    </button>
                  </div>
                </div>

                <div className="row mb-3 pt-3">
                  <div className="col-6 col-md-4 col-lg- d-flex">
                    <label className="mt-1 pull-left w-50">Theme Name</label>
                    <input
                      className={`form-control ${
                        themename ? "" : "is-invalid"
                      }`}
                      type="text"
                      placeholder="Enter Theme Name"
                      value={themename}
                      onChange={(e) => setThemeName(e.target.value)}
                    />
                    <div className="invalid-feedback">
                      Theme Name is required.
                    </div>
                  </div>
                </div>

                {COLOR_GROUPS.map((group) => (
                  <Fragment key={group.name}>
                    <div className="col-12 text-start">
                      <h6 className="pl-4">{group.name}</h6>
                    </div>
                    {renderColorInputs(group.colors)}
                  </Fragment>
                ))}

                {renderEmojiInputs()}

                <div className="d-flex justify-content-center gap-2">
                  <button type="submit" className="btn btn-sm btn-primary mb-3">
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
        </CCollapse>
      </div>
    </Fragment>
  );
}

export default Color;





























// import React, { Fragment, useEffect, useState } from "react";
// import { CCollapse, CLink } from "@coreui/react";
// import Loader from "../Loader/Loader";
// import axios from "axios";
// import config from "../../config/config";
// import { toastSuccess, toastError } from "../../common/Toast";
// import {
//   CUSTOM_SELECT_DROPDOWN_STYLE,
//   ERROR_MESSAGE,
//   SUCCESS_MESSAGE,
// } from "../../common/Constant";
// import Select from "react-select";

// function Color({ visibleBannerChange, searchQuery, dynamicModule }) {

//       const [dynamicValue, setDynamicValue] = useState("MainSite");

//       useEffect(() => {
//         if (dynamicModule) {
//           setDynamicValue(dynamicModule);
//         }
//       }, [dynamicModule]);

//   const [errors, setErrors] = useState({});
//   const [themename, setThemeName] = useState("");
//   const [themenameList, setThemeNameList] = useState([]);
//   const [id, setid] = useState(0);
//   const [themenameOptions, setthemenameOptions] = useState({
//     label: " ",
//     value: " ",
//   });
//   const [active, setActiveTheme] = useState("");
//   const [isClearable, setIsClearable] = useState(true);
//   const [primaryBaseColor, setPrimaryBaseColor] = useState("#ffffff");
//   const [primaryMainColor, setPrimaryMainColor] = useState("#ffffff");
//   const [primaryColor800, setPrimaryColor800] = useState("#ffffff");
//   const [primaryColor400, setPrimaryColor400] = useState("#ffffff");
//   const [primaryColor200, setPrimaryColor200] = useState("#ffffff");
//   const [primaryColor100, setPrimaryColor100] = useState("#ffffff");
//   const [primaryColor50, setPrimaryColor50] = useState("#ffffff");
//   const [primaryColor25, setPrimaryColor25] = useState("#ffffff");
//   const [secondaryMainColor, setSecondaryMainColor] = useState("#ffffff");
//   const [secondaryColor800, setSecondaryColor800] = useState("#ffffff");
//   const [secondaryColor500, setSecondaryColor500] = useState("#ffffff");
//   const [secondaryColor400, setSecondaryColor400] = useState("#ffffff");
//   const [secondaryColor200, setSecondaryColor200] = useState("#ffffff");
//   const [secondaryColor100, setSecondaryColor100] = useState("#ffffff");
//   const [secondaryColor50, setSecondaryColor50] = useState("#ffffff");
//   const [secondaryColor25, setSecondaryColor25] = useState("#ffffff");
//   const [secondaryColor10, setSecondaryColor10] = useState("#ffffff");
//   const [neutralMainColor, setNeutralMainColor] = useState("#ffffff");
//   const [neutralColor800, setNeutralColor800] = useState("#ffffff");
//   const [neutralColor400, setNeutralColor400] = useState("#ffffff");
//   const [neutralColor200, setNeutralColor200] = useState("#ffffff");
//   const [neutralColor100, setNeutralColor100] = useState("#ffffff");
//   const [neutralColor50, setNeutralColor50] = useState("#ffffff");
//   const [neutralColor25, setNeutralColor25] = useState("#ffffff");
//   const [neutralColor10, setNeutralColor10] = useState("#ffffff");
//   const [linkColor, setLinkColor] = useState("#ffffff");
//   const [linkColorHover, setLinkColorHover] = useState("#ffffff");
//   const [visibleBanners, setVisibleBanners] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [Emoji1, setEmoji1] = useState("");
//   const [Emoji2, setEmoji2] = useState("");
//   const [Emoji3, setEmoji3] = useState("");
//   const [selectedOption, setSelectedOption] = useState("");

//   const token = localStorage.getItem("Token");
//   const [colorthemeList, setColorThemeList] = useState([]);
//   const handleAddTheme = () => {
//     // Reset all theme-related states
//     setid("");
//     setThemeName("");
//     setthemenameOptions({
//       label: "search theme",
//       value:"search theme",
//     });
//     setIsClearable("false")
//     setPrimaryBaseColor("#ffffff");
//     setPrimaryMainColor("#ffffff");
//     setPrimaryColor800("#ffffff");
//     setPrimaryColor400("#ffffff");
//     setPrimaryColor200("#ffffff");
//     setPrimaryColor100("#ffffff");
//     setPrimaryColor50("#ffffff");
//     setPrimaryColor25("#ffffff");
//     setSecondaryMainColor("#ffffff");
//     setSecondaryColor800("#ffffff");
//     setSecondaryColor500("#ffffff");
//     setSecondaryColor400("#ffffff");
//     setSecondaryColor200("#ffffff");
//     setSecondaryColor100("#ffffff");
//     setSecondaryColor50("#ffffff");
//     setSecondaryColor25("#ffffff");
//     setSecondaryColor10("#ffffff");
//     setNeutralMainColor("#ffffff");
//     setNeutralColor800("#ffffff");
//     setNeutralColor400("#ffffff");
//     setNeutralColor200("#ffffff");
//     setNeutralColor100("#ffffff");
//     setNeutralColor50("#ffffff");
//     setNeutralColor25("#ffffff");
//     setNeutralColor10("#ffffff");
//     setLinkColor("#ffffff")
//     setLinkColorHover("#ffffff")
//     setEmoji1("");
//     setEmoji2("");
//     setEmoji3("");
//     setActiveTheme("0");
//   };

//   const themeAPiCallList = () => {
//     axios
//       .get(`${config.ecommconfigBaseUrl}/ecomconfig/colorthemelist?from=${dynamicValue}`, {
//         headers: {
//           "Content-Type": "application/json;charset=UTF-8",
//           Authorization: token,
//         },
//       })
//       .then((res) => {
//         const responseData = res.data.ResponseData || [];
//         setColorThemeList(responseData);

//         // Extract themenames from ResponseData and update the state
//         const themenames = responseData.map((item) => item.themename);
//         setThemeNameList(themenames);

//         // Find the active theme
//         const activeTheme = responseData.find((item) => item.active === 1);
//         if (activeTheme) {
//           setid(activeTheme.id);
//           setThemeName(activeTheme.themename);
//           setSelectedOption(activeTheme.themename);
//           setthemenameOptions({
//             label: activeTheme.themename,
//             value: activeTheme.themename,
//           });
//           setPrimaryBaseColor(activeTheme.primaryBaseColor);
//           setPrimaryMainColor(activeTheme.primaryMainColor);
//           setPrimaryColor800(activeTheme.primaryColor800);
//           setPrimaryColor400(activeTheme.primaryColor400);
//           setPrimaryColor200(activeTheme.primaryColor200);
//           setPrimaryColor100(activeTheme.primaryColor100);
//           setPrimaryColor50(activeTheme.primaryColor50);
//           setPrimaryColor25(activeTheme.primaryColor25);
//           setSecondaryMainColor(activeTheme.secondaryMainColor);
//           setSecondaryColor800(activeTheme.secondaryColor800);
//           setSecondaryColor500(activeTheme.secondaryColor500);
//           setSecondaryColor400(activeTheme.secondaryColor400);
//           setSecondaryColor200(activeTheme.secondaryColor200);
//           setSecondaryColor100(activeTheme.secondaryColor100);
//           setSecondaryColor50(activeTheme.secondaryColor50);
//           setSecondaryColor25(activeTheme.secondaryColor25);
//           setSecondaryColor10(activeTheme.secondaryColor10);
//           setNeutralMainColor(activeTheme.neutralMainColor);
//           setNeutralColor800(activeTheme.neutralColor800);
//           setNeutralColor400(activeTheme.neutralColor400);
//           setNeutralColor200(activeTheme.neutralColor200);
//           setNeutralColor100(activeTheme.neutralColor100);
//           setNeutralColor50(activeTheme.neutralColor50);
//           setNeutralColor25(activeTheme.neutralColor25);
//           setNeutralColor10(activeTheme.neutralColor10);
//           setLinkColor(activeTheme.linkColor);
//           setLinkColorHover(activeTheme.linkColorHover);
//           setEmoji1(activeTheme.emoji1);
//           setEmoji2(activeTheme.emoji2);
//           setEmoji3(activeTheme.emoji3);
//           setActiveTheme(activeTheme.active);
//         } else {
//           // If no active theme, clear the fields or set defaults if needed
//           // setThemeName("");
//           // setthemenameOptions({ label: "", value: "" });
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching color theme list:", error);
//       });
//   };

//   useEffect(() => {
//     themeAPiCallList();
//   }, [dynamicValue]);
//   console.log(themenameList, "themenameListthemenameList");

//   const handleThemeChange = (e) => {
//     const selectedTheme = e;
//     console.log("selectedTheme:", selectedTheme);
//     setThemeName(selectedTheme);
//     setthemenameOptions({
//       label: selectedTheme,
//       value: selectedTheme,
//     });
//     // Find the theme data from colorthemeList
//     const selectedThemeData = colorthemeList.find(
//       (theme) => theme.themename === selectedTheme
//     );

//     // Update the state with the selected theme's data
//     if (selectedThemeData) {
//       setid(selectedThemeData.id);
//       setThemeName(selectedThemeData.themename);
//       setPrimaryBaseColor(selectedThemeData.primaryBaseColor);
//       setPrimaryMainColor(selectedThemeData.primaryMainColor);
//       setPrimaryColor800(selectedThemeData.primaryColor800);
//       setPrimaryColor400(selectedThemeData.primaryColor400);
//       setPrimaryColor200(selectedThemeData.primaryColor200);
//       setPrimaryColor100(selectedThemeData.primaryColor100);
//       setPrimaryColor50(selectedThemeData.primaryColor50);
//       setPrimaryColor25(selectedThemeData.primaryColor25);
//       setSecondaryMainColor(selectedThemeData.secondaryMainColor);
//       setSecondaryColor800(selectedThemeData.secondaryColor800);
//       setSecondaryColor500(selectedThemeData.secondaryColor500);
//       setSecondaryColor400(selectedThemeData.secondaryColor400);
//       setSecondaryColor200(selectedThemeData.secondaryColor200);
//       setSecondaryColor100(selectedThemeData.secondaryColor100);
//       setSecondaryColor50(selectedThemeData.secondaryColor50);
//       setSecondaryColor25(selectedThemeData.secondaryColor25);
//       setSecondaryColor10(selectedThemeData.secondaryColor10);
//       setNeutralMainColor(selectedThemeData.neutralMainColor);
//       setNeutralColor800(selectedThemeData.neutralColor800);
//       setNeutralColor400(selectedThemeData.neutralColor400);
//       setNeutralColor200(selectedThemeData.neutralColor200);
//       setNeutralColor100(selectedThemeData.neutralColor100);
//       setNeutralColor50(selectedThemeData.neutralColor50);
//       setNeutralColor25(selectedThemeData.neutralColor25);
//       setNeutralColor10(selectedThemeData.neutralColor10);
//       setLinkColor(selectedThemeData.linkColor)
//       setLinkColorHover(selectedThemeData.linkColorHover)
//       setEmoji1(selectedThemeData.emoji1);
//       setEmoji2(selectedThemeData.emoji2);
//       setEmoji3(selectedThemeData.emoji3);
//       setActiveTheme(selectedThemeData.active);
//     }
//   };
//   const validateForm = () => {
//     const newErrors = {};
//     if (!themename.trim()) {
//       newErrors.themename = "Theme Name is required.";
//     }
//     // Add more validations for other fields as needed
//     return newErrors;
//   };

//   // console.log(primaryBaseColor,'colorthemeList')
//   const handleSubmit = async (event) => {
//     event.preventDefault();
//     const newErrors = validateForm();
//     if (Object.keys(newErrors).length > 0) {
//       setErrors(newErrors);
//       return;
//     }
//     // Create a data object with all color values
//     const data = {
//       id,
//       themename,
//       primaryBaseColor,
//       primaryMainColor,
//       primaryColor800,
//       primaryColor400,
//       primaryColor200,
//       primaryColor100,
//       primaryColor50,
//       primaryColor25,
//       secondaryMainColor,
//       secondaryColor800,
//       secondaryColor500,
//       secondaryColor400,
//       secondaryColor200,
//       secondaryColor100,
//       secondaryColor50,
//       secondaryColor25,
//       secondaryColor10,
//       neutralMainColor,
//       neutralColor800,
//       neutralColor400,
//       neutralColor200,
//       neutralColor100,
//       neutralColor50,
//       neutralColor25,
//       neutralColor10,
//       linkColor,
//       linkColorHover,
//       Emoji1,
//       Emoji2,
//       Emoji3,
//       dynamicValue
//     };
//     console.log("Color data store successfully:", data);
//     axios
//       .post(`${config.ecommconfigBaseUrl}/ecomconfig/themeupsert`, data, {
//         headers: {
//           "Content-Type": "application/json;charset=UTF-8",
//           "Access-Control-Allow-Origin": "*",
//           Authorization: token,
//         },
//       })
//       .then((res) => {
//         if (res.data.ResponseCode === "SUCCESS") {
//           toastSuccess(SUCCESS_MESSAGE);
//           // window.location.reload();
//           themeAPiCallList();
//         } else if (
//           res.data.ResponseCode === "ERROR" &&
//           res.data.ResponseID === -1
//         ) {
//           // toastError("Email" + ALREADY_EXIST);
//         } else {
//           toastError(ERROR_MESSAGE);
//         }
//         // setLoading(false);
//       })
//       .catch((error) => {
//         // setLoading(false);
//       });
//   };
//   const handleapplySubmit = async (event) => {
//     event.preventDefault();

//     // Create a data object with all color values
//     const data = {
//       id,
//       from : dynamicValue
//     };
//     console.log("apply data store successfully:", data);
//     axios
//       .post(`${config.ecommconfigBaseUrl}/ecomconfig/activetheme`, data, {
//         headers: {
//           "Content-Type": "application/json;charset=UTF-8",
//           "Access-Control-Allow-Origin": "*",
//           Authorization: token,
//         },
//       })
//       .then((res) => {
//         if (res.data.ResponseCode === "SUCCESS") {
//           toastSuccess(SUCCESS_MESSAGE);
//           themeAPiCallList();
//         } else if (
//           res.data.ResponseCode === "ERROR" &&
//           res.data.ResponseID === -1
//         ) {
//           // toastError("Email" + ALREADY_EXIST);
//         } else {
//           toastError(ERROR_MESSAGE);
//         }
//         // setLoading(false);
//       })
//       .catch((error) => {
//         // setLoading(false);
//       });
//   };

//   console.log("fetching color themename:", themename);
//   console.log("selectedOption", selectedOption);
//   console.log("themenameOptions", themenameOptions);
//   return (
//     <Fragment>
//       {/* <Loader loading={loading} /> */}

//       <div className="card">
//         <div className="card-header">
//           <h5 className="card-title">
//             <CLink
//               className="link"
//               onClick={() => setVisibleBanners(!visibleBanners)}
//             >
//               Color & Theme
//             </CLink>
//           </h5>
//         </div>
//         <CCollapse visible={visibleBanners || visibleBannerChange}>
//           <div className="card-body">
//             <form onSubmit={handleSubmit}>
//               <div className=" text-center border border-secondary border-opacity-25">
//                 <div className="row mb-3 pt-3 ">
//                   <div className="col-6 col-md-6 col-lg-2 d-flex   ">
//                     <Select
//                       id="themeSelect"
//                       className="custom-search-select w-100 mr-3 ml-3"
//                       options={themenameList.map((name) => ({
//                         value: name,
//                         label: name,
//                       }))}
//                       // value={themenameList.find(
//                       //   (option) => option.value === themename
//                       // )}
//                       value={themenameOptions}
//                       onChange={(selectedOption) => {
//                         const selectedValue = selectedOption?.value || "";
//                         // setThemeName(selectedValue);
//                         handleThemeChange(selectedValue);
//                       }}
//                       isClearable={isClearable}
//                       backspaceRemovesValue={false}
//                       controlShouldRenderValue={true}
//                       styles={CUSTOM_SELECT_DROPDOWN_STYLE}
//                     />
//                   </div>

//                   <div className="col-6 col-md-4 col-lg-3 d-flex  ">
//                     <button
//                       className="btn btn-sm btn-primary mb-3 mr-3"
//                       onClick={handleapplySubmit}
//                     >
//                       Active
//                     </button>
//                     <button
//                       type="button" // Ensures it doesn't act as a submit button
//                       className="btn btn-sm btn-primary mb-3"
//                       onClick={handleAddTheme} // Calls only the reset function
//                     >
//                       Add Theme
//                     </button>
//                   </div>
//                 </div>
//                 <div className="row mb-3 pt-3 ">
//                   <div className="col-6 col-md-4 col-lg- d-flex  ">
//                     <label className="mt-1 pull-left w-50">Theme Name</label>
//                     <input
//                       className={`form-control ${
//                         themename ? " " : "is-invalid"
//                       }`}
//                       type="text"
//                       placeholder="Enter Theme Name"
//                       value={themename}
//                       onChange={(e) => setThemeName(e.target.value)}
//                     />
//                     <div className="invalid-feedback">
//                       Theme Name is required.
//                     </div>
//                   </div>
//                 </div>
//                 {/* Primary Colors */}
//                 <div className="row mb-3 pt-3 ">
//                   <div className=" col-12 text-start">
//                     <h6 className="pl-4">Primary Colors</h6>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Primary Base Color</label>
//                     <input
//                       type="color"
//                       value={primaryBaseColor}
//                       onChange={(e) => setPrimaryBaseColor(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryBaseColor}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Primary Main Color</label>
//                     <input
//                       type="color"
//                       value={primaryMainColor}
//                       onChange={(e) => setPrimaryMainColor(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryMainColor}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary Color 800</label>
//                     <input
//                       type="color"
//                       value={primaryColor800}
//                       onChange={(e) => setPrimaryColor800(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor800}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary Color 400</label>
//                     <input
//                       type="color"
//                       value={primaryColor400}
//                       onChange={(e) => setPrimaryColor400(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor400}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary Color 200</label>
//                     <input
//                       type="color"
//                       value={primaryColor200}
//                       onChange={(e) => setPrimaryColor200(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor200}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary color 100</label>
//                     <input
//                       type="color"
//                       value={primaryColor100}
//                       onChange={(e) => setPrimaryColor100(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor100}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary color 50</label>
//                     <input
//                       type="color"
//                       value={primaryColor50}
//                       onChange={(e) => setPrimaryColor50(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor50}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>primary color 25</label>
//                     <input
//                       type="color"
//                       value={primaryColor25}
//                       onChange={(e) => setPrimaryColor25(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{primaryColor25}</p>
//                   </div>
//                 </div>

//                 {/* Secondary Colors */}
//                 <div className="row mb-3">
//                   <div className="col-12 text-start">
//                     <h6 className="pl-4">Secondary Colors</h6>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Secondary Main Color</label>
//                     <input
//                       type="color"
//                       value={secondaryMainColor}
//                       onChange={(e) => setSecondaryMainColor(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryMainColor}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Secondary Color 800</label>
//                     <input
//                       type="color"
//                       value={secondaryColor800}
//                       onChange={(e) => setSecondaryColor800(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor800}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 500</label>
//                     <input
//                       type="color"
//                       value={secondaryColor500}
//                       onChange={(e) => setSecondaryColor500(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor500}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 400</label>
//                     <input
//                       type="color"
//                       value={secondaryColor400}
//                       onChange={(e) => setSecondaryColor400(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor400}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 200</label>
//                     <input
//                       type="color"
//                       value={secondaryColor200}
//                       onChange={(e) => setSecondaryColor200(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor200}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 100</label>
//                     <input
//                       type="color"
//                       value={secondaryColor100}
//                       onChange={(e) => setSecondaryColor100(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor100}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 50</label>
//                     <input
//                       type="color"
//                       value={secondaryColor50}
//                       onChange={(e) => setSecondaryColor50(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor50}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 25</label>
//                     <input
//                       type="color"
//                       value={secondaryColor25}
//                       onChange={(e) => setSecondaryColor25(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor25}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>secondary Color 10</label>
//                     <input
//                       type="color"
//                       value={secondaryColor10}
//                       onChange={(e) => setSecondaryColor10(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{secondaryColor10}</p>
//                   </div>
//                 </div>

//                 {/* Neutral Colors */}
//                 <div className="row mb-3">
//                   <div className=" col-12 text-start">
//                     <h6 className="pl-4">Neutral Colors</h6>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Main Color</label>
//                     <input
//                       type="color"
//                       value={neutralMainColor}
//                       onChange={(e) => setNeutralMainColor(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralMainColor}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 800</label>
//                     <input
//                       type="color"
//                       value={neutralColor800}
//                       onChange={(e) => setNeutralColor800(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor800}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 400</label>
//                     <input
//                       type="color"
//                       value={neutralColor400}
//                       onChange={(e) => setNeutralColor400(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor400}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 200</label>
//                     <input
//                       type="color"
//                       value={neutralColor200}
//                       onChange={(e) => setNeutralColor200(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor200}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 100</label>
//                     <input
//                       type="color"
//                       value={neutralColor100}
//                       onChange={(e) => setNeutralColor100(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor100}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 50</label>
//                     <input
//                       type="color"
//                       value={neutralColor50}
//                       onChange={(e) => setNeutralColor50(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor50}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 25</label>
//                     <input
//                       type="color"
//                       value={neutralColor25}
//                       onChange={(e) => setNeutralColor25(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor25}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Neutral Color 10</label>
//                     <input
//                       type="color"
//                       value={neutralColor10}
//                       onChange={(e) => setNeutralColor10(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{neutralColor10}</p>
//                   </div>
//                 </div>
//                 <div className="row mb-3 pt-3">
//                   <div className="col-12 text-start">
//                     <h6 className="pl-4">Link Color</h6>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Link Color</label>
//                     <input
//                       type="color"
//                       value={linkColor}
//                       onChange={(e) => setLinkColor(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{linkColor}</p>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-2">
//                     <label>Link Color Hover</label>
//                     <input
//                       type="color"
//                       value={linkColorHover}
//                       onChange={(e) => setLinkColorHover(e.target.value)}
//                       className="form-control border-0"
//                     />
//                     <p>{linkColorHover}</p>
//                   </div>
//                   </div>
//                 <div className="row mb-3 pt-3">
//                   <div className="col-12 text-start">
//                     <h6 className="pl-4">Emoji</h6>
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-3 d-flex">
//                     <label className="mr-2  w-10 ">First Emoji</label>
//                     <input
//                       type="text"
//                        className="form-control"
//                       value={Emoji1}
//                       onChange={(e) => setEmoji1(e.target.value)}
//                     />
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-3 d-flex">
//                     <label className="mr-2  w-10  " >Second Emoji</label>
//                     <input
//                       type="text"
//                        className="form-control"
//                       value={Emoji2}
//                       onChange={(e) => setEmoji2(e.target.value)}
//                     />
//                   </div>
//                   <div className="col-6 col-md-4 col-lg-3 d-flex">
//                     <label className="mr-2 w-10 ">Third Emoji</label>
//                     <input
//                       type="text"
//                        className="form-control"
//                       value={Emoji3}
//                       onChange={(e) => setEmoji3(e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 {/* Form Submission */}
//                 <div className="d-flex justify-content-center gap-2">
//                   <button type="submit" className="btn btn-sm btn-primary mb-3">
//                     Submit
//                   </button>
//                 </div>
//               </div>
//             </form>
//           </div>
//         </CCollapse>
//       </div>
//     </Fragment>
//   );
// }

// export default Color;
